import { Bell, Heart, MessageCircle, Mail, AlertCircle, MessageSquare, X } from 'lucide-react';
import { useNotificationStore } from '../store/notificationStore';
import { format } from 'date-fns';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const NotificationDropdown = () => {
  const {
    notifications,
    unreadCount,
    isDropdownOpen,
    toggleDropdown,
    fetchNotifications,
    fetchUnreadCount,
    markAsRead,
    deleteNotification,
  } = useNotificationStore();

  const navigate = useNavigate();

  useEffect(() => {
    fetchNotifications();
    fetchUnreadCount();
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      fetchUnreadCount();
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const handleNotificationClick = (notification: typeof notifications[0]) => {
    markAsRead(notification.id);
    toggleDropdown();
    
    if (notification.related_id) {
      switch (notification.type) {
        case 'like':
        case 'reply':
          navigate(`/post/${notification.related_id}`);
          break;
        case 'message':
          navigate('/messages');
          break;
        default:
          break;
      }
    }
  };

  const getIcon = (type: string) => {
    switch (type) {
      case 'like':
        return <Heart className="w-4 h-4 text-red-500" />;
      case 'reply':
        return <MessageCircle className="w-4 h-4 text-blue-500" />;
      case 'message':
        return <Mail className="w-4 h-4 text-green-500" />;
      case 'reminder':
        return <AlertCircle className="w-4 h-4 text-yellow-500" />;
      case 'comment':
        return <MessageSquare className="w-4 h-4 text-purple-500" />;
      default:
        return <Bell className="w-4 h-4 text-gray-500" />;
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'like':
        return '点赞';
      case 'reply':
        return '回复';
      case 'message':
        return '私信';
      case 'reminder':
        return '提醒';
      case 'comment':
        return '留言';
      default:
        return '通知';
    }
  };

  return (
    <div className="relative">
      <button
        onClick={toggleDropdown}
        className="relative p-2 hover:bg-gray-100 rounded-full transition-colors"
      >
        <Bell className="w-5 h-5 text-gray-600" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {isDropdownOpen && (
        <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-xl border border-gray-100 py-2 z-50">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <h3 className="text-sm font-semibold text-gray-800">通知</h3>
            {notifications.length > 0 && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  notifications.forEach((n) => !n.is_read && markAsRead(n.id));
                }}
                className="text-xs text-blue-600 hover:text-blue-800"
              >
                全部已读
              </button>
            )}
          </div>

          <div className="max-h-80 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="px-4 py-8 text-center text-gray-400">
                <Bell className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                <p className="text-sm">暂无通知</p>
              </div>
            ) : (
              notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`flex items-start gap-3 px-4 py-3 hover:bg-gray-50 transition-colors cursor-pointer ${
                    !notification.is_read ? 'bg-gray-50' : ''
                  }`}
                  onClick={() => handleNotificationClick(notification)}
                >
                  <div className="flex-shrink-0 p-1.5 bg-gray-100 rounded-full">
                    {getIcon(notification.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between">
                      <span className="text-xs font-medium text-gray-500">
                        {getTypeLabel(notification.type)}
                      </span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteNotification(notification.id);
                        }}
                        className="p-0.5 hover:bg-gray-200 rounded transition-colors"
                      >
                        <X className="w-3 h-3 text-gray-400" />
                      </button>
                    </div>
                    <p className="text-sm text-gray-800 mt-0.5 line-clamp-2">
                      {notification.content}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      {format(new Date(notification.created_at), 'MM-dd HH:mm')}
                    </p>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationDropdown;